/**
 * The whole data model. Every element is a plain, frozen, JSON-safe object:
 * no class instances, no getters, no Maps. That keeps structuredClone (used by
 * IndexedDB) honest and makes the import validator's job well defined.
 */

export type ElementType = 'rect' | 'ellipse' | 'diamond' | 'arrow' | 'freedraw' | 'text';

/** Only two fills. A sketchy/hachure fill is a deliberate non-goal: see README. */
export type FillStyle = 'solid' | 'transparent';
export type StrokeStyle = 'solid' | 'dashed' | 'dotted';
export type FontFamily = 'sans' | 'mono';
export type TextAlign = 'left' | 'center' | 'right';

export type Point = readonly [number, number];

/** An arrow endpoint attached to another element. */
export interface Binding {
  readonly elementId: string;
  /** Distance held between the arrow tip and the target's border, in scene px. */
  readonly gap: number;
}

interface CommonProps {
  readonly id: string;
  /** Top-left of the unrotated bounding box, in scene coordinates. */
  readonly x: number;
  readonly y: number;
  /** Never negative. The reducer normalizes negative drags before they land here. */
  readonly width: number;
  readonly height: number;
  /** Radians, clockwise, about the element centre. Degrees appear only in SVG. */
  readonly angle: number;
  readonly strokeColor: string;
  readonly backgroundColor: string;
  readonly fillStyle: FillStyle;
  readonly strokeWidth: number;
  readonly strokeStyle: StrokeStyle;
  /** 0..1 */
  readonly opacity: number;
  /** Monotonic per element. Bumped on every real change; drives last-write-wins. */
  readonly version: number;
  readonly updatedAt: number;
  /** Per-tab client id of whoever last changed it. */
  readonly updatedBy: string;
  /** Tombstone: deletions must survive a merge, so they are never dropped outright. */
  readonly isDeleted: boolean;
}

export interface BoxElement extends CommonProps {
  readonly type: 'rect' | 'ellipse' | 'diamond';
}

export interface ArrowElement extends CommonProps {
  readonly type: 'arrow';
  /** Element-local coordinates, relative to (x, y). At least two points. */
  readonly points: readonly Point[];
  readonly startBinding: Binding | null;
  readonly endBinding: Binding | null;
  /** An arrow with both heads off is what other tools call a "line". */
  readonly startArrowhead: boolean;
  readonly endArrowhead: boolean;
}

export interface FreedrawElement extends CommonProps {
  readonly type: 'freedraw';
  readonly points: readonly Point[];
}

export interface TextElement extends CommonProps {
  readonly type: 'text';
  readonly text: string;
  readonly fontSize: number;
  readonly fontFamily: FontFamily;
  readonly textAlign: TextAlign;
  /** When set, this text is the label of that container and wraps to its width. */
  readonly containerId: string | null;
}

export type SlateElement = BoxElement | ArrowElement | FreedrawElement | TextElement;

export type LinearElement = ArrowElement | FreedrawElement;

export interface BoardView {
  readonly scrollX: number;
  readonly scrollY: number;
  readonly zoom: number;
}

export interface Board {
  readonly id: string;
  readonly name: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Shape of the document itself. Distinct from the IndexedDB store version. */
  readonly docVersion: number;
  readonly elements: readonly SlateElement[];
  readonly view: BoardView;
  /** Sample boards are badged in the UI and deletable in one click. */
  readonly isSample: boolean;
}

/** Document schema version. Bump only when element fields change meaning. */
export const DOC_VERSION = 1;

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;

export function isBox(el: SlateElement): el is BoxElement {
  return el.type === 'rect' || el.type === 'ellipse' || el.type === 'diamond';
}

export function isLinear(el: SlateElement): el is LinearElement {
  return el.type === 'arrow' || el.type === 'freedraw';
}

export function isArrow(el: SlateElement): el is ArrowElement {
  return el.type === 'arrow';
}

export function isText(el: SlateElement): el is TextElement {
  return el.type === 'text';
}

/** Elements an arrow is allowed to attach to. Arrows cannot bind to arrows. */
export function isBindable(el: SlateElement): el is BoxElement {
  return isBox(el) && !el.isDeleted;
}
